import axios from 'axios';
import { gcApi } from 'config/ky';
import DiTokenManager from 'auth/DiTokenManager';
import AccountSharingIndex from './AccountSharing/AccountSharingIndex';
import { isProductSupportMode, isRaceManagementMode } from 'system/SystemUtil';
import { getCookie } from 'utils/CookieUtil';
import { CONTEXT } from 'utils/NavigationUtil';
import { THEME_PREFERENCE_KEY, THEMES, getColorSchemeForTheme } from 'utils/theme/themeUtil';
import { themeRoleKey } from './backbone/models/preferences/PreferencesMapping'; // Cleanup CA-227911
import socialProfile from 'models/user/ViewerSocialProfile'; // Cleanup CA-227911
import { getHarmonySystemTheme, isHarmonyConnect } from 'utils/harmonyConnectApp/harmonyConnectAppUtil';

const axiosRoot = axios.create({
    baseURL: '/'
});

const fetchViewerUserPreferences = () => {
    return gcApi.get(`userprofile-service/userprofile/settings`);
};

const fetchViewerSocialProfile = (displayName) => {
    return gcApi.get(`userprofile-service/socialProfile/${displayName}`);
};

const fetchActorProfileImage = (garminGUID) => {
    return gcApi.get(`userprofile-service/socialProfile/image/multiple?garminGuid=${garminGUID}`);
};

const getDependencies = async () => {
    const { actor_guid } = JSON.parse(localStorage.getItem('token'));
    const [{ data: viewerUserPreferences }, { data: actorProfileImage }] = await Promise.all([
        fetchViewerUserPreferences(),
        !isProductSupportMode() && !isRaceManagementMode() && fetchActorProfileImage(actor_guid)
    ]).catch(() => {
        console.log(`Can't fetch dependencies`);
        throw new Error();
    });

    window.VIEWER_USERPREFERENCES = viewerUserPreferences;
    localStorage.setItem('viewerUserPreferences', JSON.stringify(viewerUserPreferences));
    !isProductSupportMode() &&
        !isRaceManagementMode() &&
        localStorage.setItem('actorProfileImage', JSON.stringify(actorProfileImage[0]));

    const { displayName } = viewerUserPreferences;

    const [{ data: viewerSocialProfile }] = await Promise.all([fetchViewerSocialProfile(displayName)]).catch(() => {
        console.log(`Can't fetch dependencies`);
        throw new Error();
    });
    window.VIEWER_SOCIAL_PROFILE = viewerSocialProfile;
    localStorage.setItem('viewerSocialProfile', JSON.stringify(viewerSocialProfile));
};

const fetchAccountSharingToken = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return axiosRoot.post('/services/auth/token/readonly', {
        code: urlParams.get('code'),
        codeVerifier: getCookie('code_verifier') || urlParams.get('code_verifier')
    });
};

const setupClient = async () => {
    await DiTokenManager.initializeAccountSharing(fetchAccountSharingToken);
};

const setupDelegationAuthorization = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code && code !== '') {
        try {
            await setupClient();
        } catch (e) {
            throw 'Error';
        }
    }

    await getDependencies().catch(() => {
        throw 'Error';
    });

    if (isRaceManagementMode()) {
        const view = urlParams.get('view');
        const redID = urlParams.get('redID');
        window.location.assign(`${CONTEXT}/race-management/${view}${redID ? '/' + redID : ''}`);
    } else {
        // Redirect user to Home Page
        window.location.assign(`${CONTEXT}/home`);
    }
};

const start = async () => {
    // Cleanup CA-227911
    const data = await fetch(`/modern/preference/${themeRoleKey}`).then((res) => res.json());
    const isAvailable = socialProfile.hasRole(data?.value);

    // Cleanup CA-227911
    if (isAvailable) {
        // Initialize theme
        const response = isHarmonyConnect()
            ? await getHarmonySystemTheme()
            : await gcApi.get(`/userpreference-service/${THEME_PREFERENCE_KEY}`);
        const storedTheme = localStorage.getItem('theme');
        const theme = isHarmonyConnect()
            ? response || THEMES.LIGHT
            : response.data?.value || storedTheme || THEMES.LIGHT;
        if (storedTheme !== theme) {
            localStorage.setItem('theme', theme);
        }
        document.documentElement.setAttribute('data-theme', theme);
        document.documentElement.style.setProperty('color-scheme', getColorSchemeForTheme(theme));
    }

    if (window.location.pathname.includes('delegation-authorization')) {
        // display loading screen
        AccountSharingIndex();

        // User is setting up through Account Sharing
        try {
            await setupDelegationAuthorization();
        } catch (error) {
            // can't have access to account, let user tries again
            window.location.assign(`/account-sharing`);
        }
    } else {
        // Setting up as usual
        import('App').then(({ default: App }) => {
            App();
        });
    }
};

start();
